import * as RxDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { Icon } from './Icon';

interface CommonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * `dismissible={false}` is for a modal showing something that cannot be shown again, such as a
 * temporary password: Escape and a click beside it do nothing, and there is no close button, so it
 * closes only through a button of its own in the footer.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissible = true,
}: CommonProps & { description?: string; dismissible?: boolean }) {
  const hold = (e: Event) => e.preventDefault();
  return (
    <RxDialog.Root open={open} onOpenChange={onOpenChange}>
      <RxDialog.Portal>
        <div className="office">
          <RxDialog.Overlay className="scrim">
            <RxDialog.Content
              className="modal"
              {...(!description && { 'aria-describedby': undefined })}
              {...(!dismissible && { onEscapeKeyDown: hold, onInteractOutside: hold })}
            >
              <div className="modal-h">
                {/* `grow`, as in the mockups' modal header: the close button belongs at the far right,
                    not tucked against the end of the title. */}
                <RxDialog.Title asChild>
                  <h2 className="grow">{title}</h2>
                </RxDialog.Title>
                {dismissible && (
                  <RxDialog.Close asChild>
                    <button type="button" className="ico-btn" aria-label="Close">
                      <Icon name="x" />
                    </button>
                  </RxDialog.Close>
                )}
              </div>
              {description && <RxDialog.Description className="modal-d">{description}</RxDialog.Description>}
              <div className="modal-b">{children}</div>
              {footer && <div className="modal-f">{footer}</div>}
            </RxDialog.Content>
          </RxDialog.Overlay>
        </div>
      </RxDialog.Portal>
    </RxDialog.Root>
  );
}

/**
 * `header`, when given, replaces the default single-row "title + close button" header
 * entirely — the caller owns the whole `.drawer-h` block (e.g. a multi-row header with
 * meta and progress rows) and is responsible for placing its own `RxDialog.Close`. The
 * dialog's accessible name then comes from `title` via `aria-label` rather than
 * `aria-labelledby`, since no single element holds the full title text in that case.
 */
export function Drawer({ open, onOpenChange, title, header, children, footer }: CommonProps & { header?: ReactNode }) {
  return (
    <RxDialog.Root open={open} onOpenChange={onOpenChange}>
      <RxDialog.Portal>
        <div className="office">
          <RxDialog.Overlay className="scrim">
            <RxDialog.Content className="drawer" aria-describedby={undefined} {...(header ? { 'aria-label': title } : {})}>
              {header ?? (
                <div className="drawer-h">
                  <RxDialog.Title asChild>
                    <h2>{title}</h2>
                  </RxDialog.Title>
                  <RxDialog.Close asChild>
                    <button type="button" className="ico-btn" aria-label="Close">
                      <Icon name="x" />
                    </button>
                  </RxDialog.Close>
                </div>
              )}
              {/* The one part that scrolls, so the footer's actions are always on screen. */}
              <div className="drawer-b">{children}</div>
              {footer && <div className="drawer-f">{footer}</div>}
            </RxDialog.Content>
          </RxDialog.Overlay>
        </div>
      </RxDialog.Portal>
    </RxDialog.Root>
  );
}
